import { ladderSteps, severity } from '@/domain/health';
import {
  HEALTHS,
  type ComponentConfig,
  type ComponentConfigChanges,
  type ScopeConfig,
  type ScopeConfigChanges,
  type Violation,
} from '@/domain/types';

/**
 * Validation on save (TDD §8.5). The API lambda is the authority; the mock applies the same
 * rules so the console's inline error handling can be exercised, and the form reuses
 * `validateComponentChanges` for instant feedback before submitting.
 */

/** Console contract: the placeholders the scope templates must contain. */
export const PARTIAL_RECOVERY_PLACEHOLDERS = ['{recovered}', '{remaining}'];
export const RESOLUTION_PLACEHOLDERS = ['{component}'];

const ID_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

export function validateComponentChanges(
  current: ComponentConfig,
  changes: ComponentConfigChanges,
  catalogIds?: ReadonlySet<string>,
): Violation[] {
  const next: ComponentConfig = { ...current, ...changes };
  const violations: Violation[] = [];

  if (catalogIds && !catalogIds.has(next.capability_id)) {
    violations.push({
      field: 'capability_id',
      message: `${next.capability_id} is not in the adapter's catalog, so the publisher could not find it on the page.`,
    });
  }
  if (!next.display_name?.trim()) {
    violations.push({
      field: 'display_name',
      message: 'Give a display name.',
    });
  }
  if (!next.source?.trim()) {
    violations.push({
      field: 'source',
      message: 'Name the runner.',
    });
  } else if (!ID_PATTERN.test(next.source)) {
    violations.push({
      field: 'source',
      message: 'Use 1–64 characters from A–Z, a–z, 0–9, dot, underscore, colon or hyphen.',
    });
  }

  const checks = (next.checks ?? []).map((c) => c.trim()).filter(Boolean);
  if (checks.length === 0) {
    violations.push({
      field: 'checks',
      message: 'List at least one check.',
    });
  } else if (new Set(checks).size !== checks.length) {
    violations.push({ field: 'checks', message: 'Each check name may appear only once.' });
  }

  const steps = ladderSteps(next.ladder ?? {});
  if (steps.length === 0) {
    violations.push({ field: 'ladder', message: 'The ladder needs at least one step.' });
  } else {
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i]!;
      if (!Number.isInteger(s.step) || s.step < 1) {
        violations.push({
          field: 'ladder',
          message: 'Ladder steps are whole numbers of runs, 1 or more.',
        });
        break;
      }
      if (
        !(HEALTHS as readonly string[]).includes(s.health) ||
        s.health === 'ok' ||
        s.health === 'maintenance'
      ) {
        violations.push({
          field: 'ladder',
          message: 'A ladder step maps to Degraded Performance, Partial Outage or Major Outage.',
        });
        break;
      }
      if (i > 0 && severity(s.health) <= severity(steps[i - 1]!.health)) {
        violations.push({
          field: 'ladder',
          message: `Severities must rise with the steps: ${steps[i - 1]!.step} → ${s.step} escalates backwards.`,
        });
        break;
      }
    }
    const cadence = next.runner_cadence_minutes;
    const top = steps[steps.length - 1]!;
    if (cadence > 0 && top.step * cadence > 24 * 60) {
      violations.push({
        field: 'ladder',
        message: `Step ${top.step} × ${cadence} min is more than 24 hours of wall clock; check the cadence and the steps agree.`,
      });
    }
  }

  if (!Number.isInteger(next.recovery_threshold_runs) || next.recovery_threshold_runs < 1) {
    violations.push({
      field: 'recovery_threshold_runs',
      message: 'At least 1 passing run is needed to recover.',
    });
  }
  if (!Number.isInteger(next.runner_cadence_minutes) || next.runner_cadence_minutes < 1) {
    violations.push({
      field: 'runner_cadence_minutes',
      message: 'The cadence is a whole number of minutes, 1 or more.',
    });
  }
  if (!Number.isInteger(next.staleness_tolerance_cycles) || next.staleness_tolerance_cycles < 1) {
    violations.push({
      field: 'staleness_tolerance_cycles',
      message: 'Allow at least 1 missed run before a component is blind.',
    });
  }
  if (!Number.isInteger(next.recovery_approval_alert_min) || next.recovery_approval_alert_min < 0) {
    violations.push({
      field: 'recovery_approval_alert_min',
      message: 'Use a whole number of minutes, 0 or more.',
    });
  }
  return violations;
}

export function validateScopeChanges(
  current: ScopeConfig,
  changes: ScopeConfigChanges,
): Violation[] {
  const next = { ...current, ...changes };
  const violations: Violation[] = [];
  for (const p of PARTIAL_RECOVERY_PLACEHOLDERS) {
    if (!next.partial_recovery_template.includes(p)) {
      violations.push({
        field: 'partial_recovery_template',
        message: `Must contain ${p}.`,
      });
      break;
    }
  }
  for (const p of RESOLUTION_PLACEHOLDERS) {
    if (!next.resolution_template.includes(p)) {
      violations.push({
        field: 'resolution_template',
        message: `Must contain ${p}.`,
      });
      break;
    }
  }
  return violations;
}

export function requireReason(reason: string | undefined): Violation[] {
  return reason?.trim() ? [] : [{ field: 'reason', message: 'Give a reason.' }];
}
